import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Map as MapIcon, Grid3x3, Trash2, Sprout } from "lucide-react";
import { MapContainer, TileLayer, CircleMarker, Popup, Circle } from "react-leaflet";
import { formatDate, formatXAF } from "@/lib/format";
import ParcelPhotos from "@/components/layout/ParcelPhotos";

export default function Parcelles() {
  const { t, lang } = useI18n();
  const [sites, setSites] = useState([]);
  const [parcelles, setParcelles] = useState([]);
  const [cultures, setCultures] = useState([]);
  const [plantings, setPlantings] = useState([]);
  const [plotReport, setPlotReport] = useState([]);
  const [view, setView] = useState("map");
  const [selSite, setSelSite] = useState("all");
  const [openP, setOpenP] = useState(false);
  const [openC, setOpenC] = useState(false);
  const [openPl, setOpenPl] = useState(false);
  const [newP, setNewP] = useState({ name: "", site_id: "", area_ha: "", lat: "", lng: "", soil_type: "", current_culture: "" });
  const [newC, setNewC] = useState({ name: "", scientific_name: "", cycle_days: 90, notes: "" });
  const [newPl, setNewPl] = useState({ parcelle_id: "", culture_id: "", seeded_date: "", expected_harvest_date: "", area_ha: "", quantity_seeds_kg: "" });

  async function loadAll() {
    const [s, p, c, pl, rep] = await Promise.all([
      api.get("/sites"), api.get("/parcelles"), api.get("/cultures"), api.get("/plantings"),
      api.get("/reports/plots"),
    ]);
    setSites(s.data); setParcelles(p.data); setCultures(c.data); setPlantings(pl.data);
    setPlotReport(rep.data);
  }
  useEffect(() => { loadAll(); }, []);

  const filtered = useMemo(() => parcelles.filter((p) => selSite === "all" || p.site_id === selSite), [parcelles, selSite]);
  const center = sites[0] ? [sites[0].lat, sites[0].lng] : [2.94, 9.91];

  async function addParcelle() {
    if (!newP.name || !newP.site_id) return toast.error("Nom & site requis");
    const site = sites.find((s) => s.id === newP.site_id);
    const body = { ...newP, area_ha: parseFloat(newP.area_ha || 0), lat: parseFloat(newP.lat || site?.lat || 0), lng: parseFloat(newP.lng || site?.lng || 0), site_name: site?.name, status: "cultivée" };
    await api.post("/parcelles", body);
    setOpenP(false); setNewP({ name: "", site_id: "", area_ha: "", lat: "", lng: "", soil_type: "", current_culture: "" });
    toast.success(t("added")); loadAll();
  }
  async function delParcelle(id) {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/parcelles/${id}`); toast.success(t("deleted")); loadAll();
  }
  async function addCulture() {
    if (!newC.name) return toast.error("Nom requis");
    await api.post("/cultures", { ...newC, cycle_days: parseInt(newC.cycle_days || 90) });
    setOpenC(false); setNewC({ name: "", scientific_name: "", cycle_days: 90, notes: "" });
    toast.success(t("added")); loadAll();
  }
  async function addPlanting() {
    if (!newPl.parcelle_id || !newPl.culture_id) return toast.error("Parcelle & culture requis");
    const par = parcelles.find((p) => p.id === newPl.parcelle_id);
    const cul = cultures.find((c) => c.id === newPl.culture_id);
    const seeded = newPl.seeded_date ? new Date(newPl.seeded_date) : new Date();
    const eh = newPl.expected_harvest_date ? new Date(newPl.expected_harvest_date) :
      new Date(seeded.getTime() + (cul?.cycle_days || 90) * 86400000);
    await api.post("/plantings", {
      ...newPl,
      parcelle_name: par?.name, culture_name: cul?.name,
      seeded_date: seeded.toISOString(), expected_harvest_date: eh.toISOString(),
      area_ha: parseFloat(newPl.area_ha || par?.area_ha || 0),
      quantity_seeds_kg: parseFloat(newPl.quantity_seeds_kg || 0),
    });
    setOpenPl(false); setNewPl({ parcelle_id: "", culture_id: "", seeded_date: "", expected_harvest_date: "", area_ha: "", quantity_seeds_kg: "" });
    toast.success(t("added")); loadAll();
  }

  return (
    <div className="space-y-6" data-testid="parcelles-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-2">{t("farm_sites")}</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">{t("parcelles")}</h1>
          <p className="text-muted-foreground mt-1">
            {sites.length} sites · {parcelles.length} parcelles · {parcelles.reduce((s, p) => s + (p.area_ha || 0), 0).toFixed(1)} ha
          </p>
        </div>
        <div className="flex gap-2">
          <Dialog open={openC} onOpenChange={setOpenC}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-culture-btn"><Sprout className="w-4 h-4 mr-2" />{t("culture")}</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{t("add")} — {t("culture")}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>{t("name")}</Label><Input data-testid="culture-name" value={newC.name} onChange={(e) => setNewC({ ...newC, name: e.target.value })} /></div>
                <div><Label>{t("scientific_name")}</Label><Input value={newC.scientific_name} onChange={(e) => setNewC({ ...newC, scientific_name: e.target.value })} /></div>
                <div><Label>{t("cycle_days")}</Label><Input type="number" value={newC.cycle_days} onChange={(e) => setNewC({ ...newC, cycle_days: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={addCulture} data-testid="culture-save">{t("save")}</Button></DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={openPl} onOpenChange={setOpenPl}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-planting-btn"><Sprout className="w-4 h-4 mr-2" />{t("new_planting")}</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{t("new_planting")}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>{t("parcelle")}</Label>
                  <Select value={newPl.parcelle_id} onValueChange={(v) => setNewPl({ ...newPl, parcelle_id: v })}>
                    <SelectTrigger data-testid="planting-parcelle"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{parcelles.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t("culture")}</Label>
                  <Select value={newPl.culture_id} onValueChange={(v) => setNewPl({ ...newPl, culture_id: v })}>
                    <SelectTrigger data-testid="planting-culture"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{cultures.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>{t("seeded_date")}</Label><Input type="date" value={newPl.seeded_date} onChange={(e) => setNewPl({ ...newPl, seeded_date: e.target.value })} /></div>
                <div><Label>{t("area")}</Label><Input type="number" step="0.1" value={newPl.area_ha} onChange={(e) => setNewPl({ ...newPl, area_ha: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={addPlanting} data-testid="planting-save">{t("save")}</Button></DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={openP} onOpenChange={setOpenP}>
            <DialogTrigger asChild>
              <Button data-testid="add-parcelle-btn"><Plus className="w-4 h-4 mr-2" />{t("parcelle")}</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{t("add")} — {t("parcelle")}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>{t("name")}</Label><Input data-testid="parcelle-name" value={newP.name} onChange={(e) => setNewP({ ...newP, name: e.target.value })} /></div>
                <div className="col-span-2">
                  <Label>{t("site")}</Label>
                  <Select value={newP.site_id} onValueChange={(v) => setNewP({ ...newP, site_id: v })}>
                    <SelectTrigger data-testid="parcelle-site"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>{t("area")}</Label><Input type="number" step="0.1" value={newP.area_ha} onChange={(e) => setNewP({ ...newP, area_ha: e.target.value })} /></div>
                <div><Label>Sol</Label><Input value={newP.soil_type} onChange={(e) => setNewP({ ...newP, soil_type: e.target.value })} /></div>
                <div><Label>Lat</Label><Input type="number" step="0.0001" value={newP.lat} onChange={(e) => setNewP({ ...newP, lat: e.target.value })} /></div>
                <div><Label>Lng</Label><Input type="number" step="0.0001" value={newP.lng} onChange={(e) => setNewP({ ...newP, lng: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={addParcelle} data-testid="parcelle-save">{t("save")}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <Select value={selSite} onValueChange={setSelSite}>
          <SelectTrigger className="w-56" data-testid="site-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("all_sites")}</SelectItem>
            {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.area_ha} ha)</SelectItem>)}
          </SelectContent>
        </Select>
        <Tabs value={view} onValueChange={setView}>
          <TabsList>
            <TabsTrigger value="map" data-testid="tab-map"><MapIcon className="w-4 h-4 mr-1" />{t("map_view")}</TabsTrigger>
            <TabsTrigger value="grid" data-testid="tab-grid"><Grid3x3 className="w-4 h-4 mr-1" />{t("grid_view")}</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {view === "map" ? (
        <Card className="border-border overflow-hidden">
          <div className="h-[520px]">
            <MapContainer center={center} zoom={15} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
              <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {sites.map((s) => (
                <Circle key={s.id} center={[s.lat, s.lng]} radius={220} pathOptions={{ color: "#1E3F20", fillOpacity: 0.08 }}>
                  <Popup>{s.name} — {s.area_ha} ha</Popup>
                </Circle>
              ))}
              {filtered.map((p) => (
                <CircleMarker key={p.id} center={[p.lat, p.lng]} radius={12}
                  pathOptions={{ color: "#CC5500", fillColor: "#DDA15E", fillOpacity: 0.9 }}>
                  <Popup>
                    <div className="text-sm">
                      <div className="font-semibold">{p.name}</div>
                      <div>{p.area_ha} ha · {p.current_culture || "—"}</div>
                      <div className="text-xs opacity-70">{p.site_name}</div>
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
          {filtered.map((p) => (
            <Card key={p.id} className="card-hover border-border" data-testid={`parcelle-card-${p.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="label-eyebrow">{p.site_name}</div>
                    <div className="text-xl font-bold mt-1">{p.name}</div>
                  </div>
                  <Badge variant="secondary">{p.area_ha} ha</Badge>
                </div>
                <div className="mt-4 text-sm space-y-1 text-muted-foreground">
                  <div>{t("culture")}: <span className="text-foreground font-medium">{p.current_culture || "—"}</span></div>
                  <div>Sol: <span className="text-foreground">{p.soil_type || "—"}</span></div>
                  <div>{t("status")}: <span className="text-foreground">{p.status || "—"}</span></div>
                </div>
                <ParcelPhotos parcelleId={p.id} />
                <div className="mt-4 flex justify-end">
                  <Button variant="ghost" size="sm" onClick={() => delParcelle(p.id)} data-testid={`parcelle-delete-${p.id}`}>
                    <Trash2 className="w-4 h-4 mr-1" />{t("delete")}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card className="border-border">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4">{t("plantings")}</h3>
          {plantings.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">{t("no_data")}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left border-b border-border">
                  <tr>
                    <th className="py-2">{t("parcelle")}</th>
                    <th>{t("culture")}</th>
                    <th>{t("seeded_date")}</th>
                    <th>{t("expected_harvest")}</th>
                    <th>{t("area")}</th>
                  </tr>
                </thead>
                <tbody>
                  {plantings.map((p) => (
                    <tr key={p.id} className="border-b border-border/60 last:border-0">
                      <td className="py-2 font-medium">{p.parcelle_name}</td>
                      <td>{p.culture_name}</td>
                      <td>{formatDate(p.seeded_date, lang)}</td>
                      <td>{formatDate(p.expected_harvest_date, lang)}</td>
                      <td>{p.area_ha} ha</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      <Card className="border-border" data-testid="plot-rentability">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">{t("rentability")} — {t("parcelles")}</h3>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/exports/report.xlsx?type=plots`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" data-testid="export-plots-btn">{t("export_excel")}</Button>
            </a>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left border-b border-border">
                <tr>
                  <th className="py-2">{t("parcelle")}</th>
                  <th>{t("culture")}</th>
                  <th>{t("expected_cost")}</th>
                  <th>{t("actual_cost")}</th>
                  <th>{t("cost_gap")}</th>
                  <th>{t("expected_yield")}</th>
                  <th>{t("actual_yield")}</th>
                  <th>{t("yield_gap")}</th>
                  <th>{t("status")}</th>
                </tr>
              </thead>
              <tbody>
                {plotReport.map((r) => (
                  <tr key={r.planting_id} className="border-b border-border/60 last:border-0" data-testid={`plot-report-${r.planting_id}`}>
                    <td className="py-2 font-medium">{r.parcelle_name}</td>
                    <td>{r.culture_name}</td>
                    <td>{formatXAF(r.expected_cost)}</td>
                    <td>{formatXAF(r.actual_cost)}</td>
                    <td className={r.cost_gap > 0 ? "text-destructive font-medium" : "text-primary font-medium"}>
                      {r.cost_gap > 0 ? "+" : ""}{formatXAF(r.cost_gap)}
                    </td>
                    <td>{r.expected_yield_t} t</td>
                    <td>{r.actual_yield_t != null ? `${r.actual_yield_t} t` : "—"}</td>
                    <td className={r.yield_gap != null && r.yield_gap < 0 ? "text-destructive font-medium" : "text-primary font-medium"}>
                      {r.yield_gap != null ? `${r.yield_gap > 0 ? "+" : ""}${r.yield_gap} t` : "—"}
                    </td>
                    <td><Badge variant={r.status === "récoltée" ? "default" : "secondary"}>{t(r.status) || r.status}</Badge></td>
                  </tr>
                ))}
                {plotReport.length === 0 && (
                  <tr><td colSpan={9} className="py-6 text-center text-sm text-muted-foreground">{t("no_data")}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
